import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Breadcrumbs from '../components/Breadcrumbs';
import GroupedProofGallery from '../components/GroupedProofGallery';
import ReportBuilderDialog from '../components/ReportBuilderDialog';
import { StatCard, StatusBar, StatChips, DamageGauge, damageTone } from '../components/metrics';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { SITE_STATUS, ACTION_LABELS, POSITIONS } from '../lib/status';
import { Megaphone, Loader2, Layers, TriangleAlert, FileText, FileSpreadsheet, Users, Images, Activity, Settings2, Save, Ban, RotateCcw, CalendarClock, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';

export default function CampaignDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canManage = ['admin', 'manager'].includes(user.role);
  const [campaign, setCampaign] = useState(null);
  const [report, setReport] = useState(null);
  const [proofs, setProofs] = useState(null);
  const [exporting, setExporting] = useState('');
  const [mapOpen, setMapOpen] = useState(false);
  const [datesOpen, setDatesOpen] = useState(false);
  const [inspections, setInspections] = useState([]);

  const loadCampaign = useCallback(() => api.get('/campaigns').then((r) => setCampaign(r.data.find((c) => c.id === id) || false)), [id]);
  useEffect(() => {
    loadCampaign();
    api.get(`/reports/summary?campaign_id=${id}&type=all`).then((r) => setReport(r.data));
    api.get(`/proofs?campaign_id=${id}&limit=1000`).then((r) => setProofs(r.data.proofs)).catch(() => setProofs([]));
    api.get('/inspections').then((r) => setInspections(r.data.filter((i) => (i.campaign_ids || []).includes(id) || i.campaign_id === id))).catch(() => setInspections([]));
  }, [id, loadCampaign]);

  const doExport = async (fmt) => {
    setExporting(fmt);
    try {
      const res = await api.get(`/reports/export?campaign_id=${id}&type=all&fmt=${fmt}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `campaign_report.${fmt === 'excel' ? 'xlsx' : 'pdf'}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
    setExporting('');
  };

  const toggleStatus = async () => {
    const action = campaign.status === 'cancelled' ? 'reactivate' : 'cancel';
    if (action === 'cancel' && !window.confirm(`Cancel ${campaign.name}? Its sites become vacant.`)) return;
    await api.post(`/campaigns/${id}/${action}`);
    loadCampaign(); toast.success(action === 'cancel' ? 'Campaign cancelled' : 'Campaign reactivated');
  };

  if (campaign === false) return <p className="text-muted-foreground">Campaign not found or not permitted.</p>;
  if (!campaign || !report) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  const t = report.totals;
  const timeline = (proofs || []).slice(0, 12);

  return (
    <div className="space-y-5">
      <Breadcrumbs items={[{ label: 'Campaigns', to: '/campaigns' }, { label: campaign.name }]} />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="overline text-primary flex items-center gap-2"><Megaphone className="h-3.5 w-3.5" /> Campaign</p>
          <h1 className="font-head text-3xl font-bold">{campaign.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {campaign.client_user_id
              ? <Link to={`/clients/${campaign.client_user_id}`} data-testid="camp-client-link" className="flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs hover:border-primary hover:text-primary"><Users className="h-3.5 w-3.5" /> {campaign.client_name}</Link>
              : <span className="flex items-center gap-1 text-xs"><Users className="h-3.5 w-3.5" /> {campaign.client_name}</span>}
            <span className="font-mono text-xs">{campaign.start_date || '—'} → {campaign.end_date || '—'}</span>
            {campaign.status === 'cancelled' && <span className="rounded-full bg-rose-500/20 px-2 py-0.5 text-[10px] uppercase text-rose-300">Cancelled</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <ReportBuilderDialog scope={{ campaign_id: id }} groupByDefault="society" defaults={{ title: `${campaign.name} - Inspection Report`, client_name: campaign.client_name }} />
          {canManage && <Button variant="secondary" data-testid="camp-manage-sites" onClick={() => setMapOpen(true)}><Settings2 className="mr-1 h-4 w-4" /> Manage Sites</Button>}
          {canManage && <Button variant="secondary" data-testid="camp-revise-dates" onClick={() => setDatesOpen(true)}><CalendarClock className="mr-1 h-4 w-4" /> Revise Dates</Button>}
          {canManage && (campaign.status === 'cancelled'
            ? <Button variant="secondary" data-testid="camp-reactivate" onClick={toggleStatus}><RotateCcw className="mr-1 h-4 w-4" /> Reactivate</Button>
            : <Button variant="secondary" className="text-rose-400" data-testid="camp-cancel" onClick={toggleStatus}><Ban className="mr-1 h-4 w-4" /> Cancel</Button>)}
          <Button variant="secondary" data-testid="camp-export-pdf" onClick={() => doExport('pdf')} disabled={!!exporting}>{exporting === 'pdf' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileText className="mr-1 h-4 w-4" />} PDF</Button>
          <Button variant="secondary" data-testid="camp-export-excel" onClick={() => doExport('excel')} disabled={!!exporting}>{exporting === 'excel' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-1 h-4 w-4" />} Excel</Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-4">
        <div className="grid grid-cols-2 gap-3 lg:col-span-3">
          <StatCard label="Sites" value={t.total} icon={Layers} />
          <StatCard label="Installed" value={`${t.total ? Math.round((t.installed / t.total) * 100) : 0}%`} tone="text-blue-400" />
          <StatCard label="Damaged" value={t.damaged} icon={TriangleAlert} tone={damageTone(t.damage_pct)} />
          <StatCard label="Damage" value={`${t.damage_pct}%`} tone={damageTone(t.damage_pct)} />
        </div>
        <div className="rounded-xl border border-border bg-card p-4"><DamageGauge stats={{ damage_pct: t.damage_pct, health: 100 - t.damage_pct }} /></div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <StatusBar counts={report.status_counts} total={t.total} className="mb-3" />
        <StatChips counts={report.status_counts} />
      </div>

      <Tabs defaultValue="proofs">
        <TabsList>
          <TabsTrigger value="proofs" data-testid="cd-tab-proofs"><Images className="mr-1 h-3.5 w-3.5" />Proof Gallery</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="cd-tab-timeline"><Activity className="mr-1 h-3.5 w-3.5" />Activity</TabsTrigger>
          <TabsTrigger value="inspections" data-testid="cd-tab-inspections"><ClipboardList className="mr-1 h-3.5 w-3.5" />Inspections</TabsTrigger>
        </TabsList>
        <TabsContent value="proofs" className="mt-4" data-testid="campaign-proof-gallery">
          {proofs === null ? <Loader2 className="h-5 w-5 animate-spin text-primary" />
            : <GroupedProofGallery proofs={proofs} allowedGroupings={['society_tower', 'society', 'lift', 'site_type', 'inspection', 'action', 'operator']} initialGroupBy="society_tower" emptyMessage="No proof photos captured for this campaign yet." />}
        </TabsContent>
        <TabsContent value="timeline" className="mt-4">
          <div className="space-y-2">
            {timeline.map((p) => { const c = SITE_STATUS[p.new_status] || {}; return (
              <div key={p.id} data-testid={`cd-timeline-${p.id}`} className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: c.dot }} />
                <div className="min-w-0 flex-1"><p className="text-sm font-medium">{ACTION_LABELS[p.action_type]} · {POSITIONS[p.position_type]}</p><p className="truncate text-xs text-muted-foreground">{p.society_name} · {p.tower_name} · {p.lift_name}</p></div>
                <span className="font-mono text-[10px] text-muted-foreground">{(p.timestamp || '').slice(0, 16).replace('T', ' ')}</span>
              </div>
            ); })}
            {!timeline.length && <p className="py-6 text-center text-sm text-muted-foreground">No activity recorded for this campaign yet.</p>}
          </div>
        </TabsContent>
        <TabsContent value="inspections" className="mt-4">
          <div className="grid gap-3 md:grid-cols-2" data-testid="cd-inspections-list">
            {inspections.map((i) => (
              <Link key={i.id} to={`/inspections/${i.id}`} data-testid={`cd-inspection-${i.id}`} className="rounded-xl border border-border bg-card p-4 transition hover:border-primary">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-head text-base font-bold">{i.name || i.society_name}</p>
                  {i.kind === 'deinstall' && <span className="rounded-full border border-amber-600 bg-amber-950/50 px-2 py-0.5 text-[9px] font-mono uppercase text-amber-300">Deinstallation</span>}
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">{i.society_name} · {i.site_ids?.length || 0} sites{i.operator_name ? ` · ${i.operator_name}` : ''}</p>
              </Link>
            ))}
            {!inspections.length && <p className="py-6 text-center text-sm text-muted-foreground md:col-span-2">No inspections recorded for this campaign yet.</p>}
          </div>
        </TabsContent>
      </Tabs>

      {mapOpen && <ManageSitesDialog campaign={campaign} onClose={() => setMapOpen(false)} onSaved={loadCampaign} />}
      {datesOpen && <ReviseDatesDialog campaign={campaign} onClose={() => setDatesOpen(false)} onSaved={loadCampaign} />}
    </div>
  );
}

function ReviseDatesDialog({ campaign, onClose, onSaved }) {
  const [start, setStart] = useState(campaign.start_date || '');
  const [end, setEnd] = useState(campaign.end_date || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!start || !end) return toast.error('Start and end dates are required');
    if (end < start) return toast.error('End date must be on or after start date');
    setSaving(true);
    try {
      await api.patch(`/campaigns/${campaign.id}`, {
        name: campaign.name, client_name: campaign.client_name, client_user_id: campaign.client_user_id,
        is_installation: !!campaign.is_installation, site_type: campaign.site_type || 'mixed', lift_side: campaign.lift_side || 'all',
        society_ids: campaign.society_ids || [], site_ids: campaign.site_ids || [],
        start_date: start, end_date: end,
      });
      toast.success('Campaign dates updated'); onSaved(); onClose();
    } catch { toast.error('Failed to update dates'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head text-2xl">Revise Campaign Dates</DialogTitle><DialogDescription>Update the start and end dates for {campaign.name}. Occupancy is recalculated from these dates.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Start <span className="text-rose-400">*</span></Label><Input type="date" data-testid="revise-start" value={start} onChange={(e) => setStart(e.target.value)} /></div>
          <div><Label>End <span className="text-rose-400">*</span></Label><Input type="date" data-testid="revise-end" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving || !start || !end} data-testid="save-dates-btn">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save Dates</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageSitesDialog({ campaign, onClose, onSaved }) {
  const [sites, setSites] = useState(null);
  const [sel, setSel] = useState(campaign.site_ids || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const socs = campaign.society_ids || [];
    if (!socs.length) return setSites([]);
    const st = campaign.site_type;
    const ls = campaign.lift_side;
    const sideOk = (side) => !ls || ls === 'all' || side === ls;
    const typeOk = (pt) => !st || st === 'mixed' ? true : (st === 'poster' ? (pt === 'poster_left' || pt === 'poster_right') : (pt === 'door_vinyl' || pt === 'door_side'));
    Promise.all(socs.map((soc) => api.get(`/societies/${soc}/layout`).then((r) => r.data).catch(() => null))).then((layouts) => {
      const flat = [];
      layouts.filter(Boolean).forEach((d) => {
        const socName = d.society?.name || '';
        (d.towers || []).forEach((t) => t.lifts.forEach((l) => { if (sideOk(l.side)) l.sites.forEach((s) => { if (typeOk(s.position_type)) flat.push({ ...s, society: socName, tower: t.name, lift: l.name }); }); }));
      });
      setSites(flat);
    });
  }, [campaign]);

  const save = async () => {
    setSaving(true);
    try { await api.put(`/campaigns/${campaign.id}/eligible-sites`, { site_ids: sel }); toast.success('Eligible sites updated'); onSaved(); onClose(); }
    catch { toast.error('Failed'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head text-2xl">Manage Eligible Sites</DialogTitle><DialogDescription>Choose which site positions this campaign is allowed to occupy.</DialogDescription></DialogHeader>
        {sites === null ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : (
          <div className="max-h-96 space-y-1 overflow-y-auto rounded-lg border border-border p-2">
            {sites.map((s) => (
              <label key={s.id} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-secondary">
                <Checkbox checked={sel.includes(s.id)} data-testid={`map-site-${s.id}`} onCheckedChange={() => setSel((p) => p.includes(s.id) ? p.filter((x) => x !== s.id) : [...p, s.id])} />
                <span className="flex-1">{s.society} · {s.tower} · {s.lift} · {POSITIONS[s.position_type]}</span>
                {s.current_campaign_id && s.current_campaign_id !== campaign.id && <span className="rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[9px] uppercase text-amber-300">occupied</span>}
              </label>
            ))}
            {!sites.length && <p className="px-2 py-1 text-xs text-muted-foreground">No sites in the mapped society.</p>}
          </div>
        )}
        <DialogFooter><Button onClick={save} disabled={saving} data-testid="save-eligible-btn">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save ({sel.length})</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
